import { Injectable } from "@angular/core";
import { Observable, BehaviorSubject } from "rxjs";
import { UsuarioGuiasService } from "./data-services/usuario.guias.service";
import { UsuarioApontamentosService } from "./data-services/usuario.apontamentos.service";
import { Guia } from "../models/Guia";
import { LeiRepositorio } from "../repositorios/lei.repositorio";
import { Apontamento } from "../models/Apontamento";
import { ApontamentoListaPorData } from "../models/ApontamentoListaPorData";
import { ErrorHandlerService } from "./errorHandler.service";

@Injectable()
export class TimerService {
  private titulo = "";
  private guiasIdLeis = "";
  private apontamentos: Apontamento[] = [];
  private apontamentos$ = new BehaviorSubject<Apontamento[]>(this.apontamentos);
  private apontamentoAtual: Apontamento = new Apontamento();
  private apontamentoAtual$ = new BehaviorSubject<Apontamento>(
    this.apontamentoAtual
  );
  private apontamentosListaPorData: ApontamentoListaPorData[] = [];
  private apontamentosListaPorData$ = new BehaviorSubject<
    ApontamentoListaPorData[]
  >(this.apontamentosListaPorData);

  constructor(
    private usuarioGuiaService: UsuarioGuiasService,
    private usuarioApontamentosService: UsuarioApontamentosService,
    private leiRepositorio: LeiRepositorio,
    private errorHandlerService: ErrorHandlerService
  ) {
    this.usuarioGuiaService.getGuiaAtiva().subscribe((guia) => {
      this.guiasSubscribe(guia);
    });
  }

  public carregarApontamento(guiasIdLeis: string) {
    this.apontamentoAtual = new Apontamento();
    this.usuarioApontamentosService
      .buscarId(guiasIdLeis)
      .then((apontamentos) => {
        const hoje = new Date();
        const dia = hoje.getDate();
        const mes = hoje.getMonth() + 1;
        const ano = hoje.getFullYear();
        this.apontamentoAtual =
          apontamentos.find(
            (ap) => ap.dia === dia && ap.mes === mes && ap.ano === ano
          ) ?? new Apontamento();
        this.apontamentoAtual.descricaoConteudo = this.titulo;

        this.updateApontamentoAtual();
      });
  }

  public carregarApontamentosLei(guiasIdLeis: string) {
    this.apontamentos = [];
    this.usuarioApontamentosService
      .buscarId(guiasIdLeis)
      .then((apontamentos) => {
        this.apontamentos = apontamentos;
        this.apontamentos.sort(
          (a, b) =>
            new Date(b.dataHoraModificacao).getTime() -
            new Date(a.dataHoraModificacao).getTime()
        );

        this.updateApontamentos();
      });
  }

  public carregarApontamentosData() {
    this.apontamentos = [];
    this.usuarioApontamentosService.buscar().then((apontamentos) => {
      const hoje = new Date();
      const dia = hoje.getDate();
      const mes = hoje.getMonth() + 1;
      const ano = hoje.getFullYear();

      apontamentos = apontamentos.filter(
        (apontamento) =>
          apontamento?.controleTempo?.length > 0 &&
          apontamento?.anotacoes?.length > 0
      );

      apontamentos
        .filter((ap) => ap.dia === dia && ap.mes === mes && ap.ano === ano)
        .map((ap) => {
          this.leiRepositorio
            .carregarItemLookup(ap.idLei)
            .then((lei) => (ap.descricaoConteudo = lei.titulo))
            .catch((error) => this.errorHandlerService.handleError(error));
          this.apontamentos.push(ap);
        });

      this.apontamentos.sort(
        (a, b) =>
          new Date(b.dataHoraModificacao).getTime() -
          new Date(a.dataHoraModificacao).getTime()
      );

      this.updateApontamentos();
    });
  }

  public carregarApontamentosListaPorData() {
    this.apontamentosListaPorData = [];
    this.usuarioApontamentosService.buscar().then((apontamentos) => {
      apontamentos = apontamentos.filter(
        (apontamento) =>
          apontamento?.controleTempo?.length > 0 &&
          apontamento?.anotacoes?.length > 0 &&
          apontamento?.idLei
      );

      apontamentos.sort(
        (a, b) =>
          new Date(b.dataHoraModificacao).getTime() -
          new Date(a.dataHoraModificacao).getTime()
      );

      apontamentos.map((ap) => {
        this.leiRepositorio
          .carregarItemLookup(ap.idLei)
          .then((lei) => (ap.descricaoConteudo = lei.titulo))
          .catch((error) => this.errorHandlerService.handleError(error));
        return ap;
      });

      const datasApontamento = apontamentos.map((apontamento) =>
        new Date(apontamento.dataHoraModificacao).toDateString()
      );

      const unique = [...new Set(datasApontamento)];

      unique.forEach((data) => {
        const normalizeData = new Date(data);

        this.apontamentosListaPorData.push({
          dataApontamento: normalizeData,
          apontamentos: apontamentos.filter(
            (apontamento) =>
              new Date(apontamento.dataHoraModificacao).getDate() ===
                normalizeData.getDate() &&
              new Date(apontamento.dataHoraModificacao).getMonth() ===
                normalizeData.getMonth() &&
              new Date(apontamento.dataHoraModificacao).getFullYear() ===
                normalizeData.getFullYear()
          ),
        });
      });
      this.updateApontamentosListaPorData();
    });
  }

  public async salvarApontamento(apontamento: Apontamento) {
    apontamento.idLei = this.guiasIdLeis;
    await this.usuarioApontamentosService.atualizar(apontamento);
    this.carregarApontamentosData();
    this.carregarApontamentosListaPorData();
  }

  public async atualizarApontamento(apontamento: Apontamento) {
    await this.usuarioApontamentosService.atualizar(apontamento);
    this.carregarApontamentosData();
    this.carregarApontamentosListaPorData();
  }

  updateApontamentosListaPorData() {
    this.apontamentosListaPorData$.next(this.apontamentosListaPorData);
  }

  getApontamentosListaPorData(): Observable<ApontamentoListaPorData[]> {
    return this.apontamentosListaPorData$.asObservable();
  }
  updateApontamentos() {
    this.apontamentos$.next(this.apontamentos);
  }

  getApontamentos(): Observable<Apontamento[]> {
    return this.apontamentos$.asObservable();
  }

  updateApontamentoAtual() {
    this.apontamentoAtual$.next(this.apontamentoAtual);
  }

  getApontamentoAtual(): Observable<Apontamento> {
    return this.apontamentoAtual$.asObservable();
  }

  private guiasSubscribe(guia: Guia) {
    if (!guia?.idLei) {
      return;
    }

    if (this.guiasIdLeis !== guia.idLei) {
      this.carregarApontamentosListaPorData();
      this.carregarApontamentosData();
    }
    this.guiasIdLeis = guia.idLei;
    this.titulo = guia.titulo;
    this.carregarApontamento(this.guiasIdLeis);
  }
}
